<script lang="ts" setup>
import NewHeader from '@/components/NewHeader.vue';
import NewFooter from '@/components/NewFooter.vue';
import LoginWithGoogle from '~/components/LoginWithGoogle.vue';
import api from '~/api';
import PwdInput from '~/components/PwdInput.vue';

const snackbar = useSnackbarStore();
const utmStore = useUtmStore();
const utils = useUtils();
const localePath = useLocalePath();
const router = useRouter();
const { t } = useI18n();

const globalLoading = useLoadingStore();
const loginFormVisible = ref(true);
const openEmailLoginForm = ref(false); // 控制 email 登录表单显示
const email = ref('');
const password = ref('');
const verificationCode = ref('');
const countdown = ref(0); // 倒计时秒数
let countdownInterval: number | null = null;

const isCountdownActive = computed(() => countdown.value > 0);

const emailRules = [
    (v: string) => !!v || t('common.email_required'),
    (v: string) => /.+@.+\..+/.test(v) || t('common.email_valid'),
];

// 发送验证码
async function sendCode() {
    const isValidEmail = emailRules.every((rule) => rule(email.value) === true);
    if (!isValidEmail) {
        snackbar.showError(t('common.email_valid'));
        return;
    }

    try {
        globalLoading.startLoading();

        // utmAll: new URLSearchParams(utmStore.utmParams).toString()
        await api.registerByEmail(email.value, password.value, new URLSearchParams(utmStore.utmParams).toString());
        startCountdown(60); // 开始倒计时 60 秒
        snackbar.showSuccess(t('common.verification_sent'));
    } catch (error) {
        snackbar.showError(error.response?.data?.message || t('common.failed_send_code'));
    } finally {
        globalLoading.stopLoading();
    }
}

// 倒计时函数
function startCountdown(seconds: number) {
    countdown.value = seconds;
    countdownInterval = window.setInterval(() => {
        if (countdown.value > 0) {
            countdown.value--;
        } else {
            clearInterval(countdownInterval!);
            countdownInterval = null;
        }
    }, 1000);
}

// 注册处理
async function onSignUp() {
    if (!email.value || !password.value || !verificationCode.value) {
        snackbar.showError(t('common.fill_all_fields'));
        return;
    }
    try {
        globalLoading.startLoading();
        const resp = await api.emailActivate(verificationCode.value);
        snackbar.showSuccess(t('common.registration_successful'));
        await utils.loginByToken(resp.token, '/my/account');
    } catch (error) {
        snackbar.showError(error.response?.data.message || error.response?.message || t('common.failed_signup'));
    } finally {
        globalLoading.stopLoading();
    }
}
</script>

<template>
    <div>
        <NewHeader />
        <div class="flex flex-1 bg-[url('@/assets/images/login-bg.png')] bg-cover relative min-h-dvh items-center">
            <div class="back-mask w-full h-full flex items-center justify-center bg-cover overflow-auto">
                <div v-if="loginFormVisible" class="m-auto flex flex-col items-center bg-neutral-900 p-5 lg:p-7 rounded-2xl mx-4 max-w-[450px] my-20 lg:my-24">
                    <NuxtLink :to="localePath('/')" class="flex items-center">
                        <img class="h-8 w-8" src="/idx/bestfaceswap-logo.svg" alt="BestFaceSwap logo" />
                    </NuxtLink>
                    <div class="title font-bold text-center text-xl my-5">{{ t('common.signup_title') }}</div>
                    <LoginWithGoogle></LoginWithGoogle>
                    <div class="separator flex items-center gap-2 my-2 w-full">
                        <hr class="flex-1 border-neutral-600" />
                        <span class="text-neutral-500">{{ t('common.signup_or') }}</span>
                        <hr class="flex-1 border-neutral-600" />
                    </div>
                    <v-btn v-if="!openEmailLoginForm" @click="openEmailLoginForm = !openEmailLoginForm" class="w-full bg-white/10 text-white max-w-[425px] py-3 rounded-full text-lg font-semibold my-5">
                        <img src="/public/icon/email.svg" class="h-6 w-6 mr-2" alt="Email Icon" />
{{ t('common.continue_email') }}
                    </v-btn>
                    <v-expand-transition>
                        <div v-if="openEmailLoginForm" class="w-full">
                            <!-- Email 输入框 -->
                            <v-text-field v-model="email" :rules="emailRules" :label="t('common.email')" outlined dense hide-details class="mb-4"></v-text-field>
                            <!-- 密码输入框 -->
                            <PwdInput v-model="password" :label="t('common.password')" :placeholder="t('common.password_placeholder')" />
                            <!-- 验证码输入和发送按钮 -->
                            <div class="flex items-center">
                                <v-text-field v-model="verificationCode" :label="t('common.verification_code')" outlined dense hide-details class="flex-1 verification-code"></v-text-field>
                                <v-btn :disabled="isCountdownActive" @click="sendCode" class="bg-orange-600 text-neutral-950 font-semibold rounded-r-full h-14">
                                    {{ countdown > 0 ? `${t('common.resend')}(${countdown}s)` : t('common.send_code') }}
                                </v-btn>
                            </div>
                            <!-- 注册按钮 -->
                            <v-btn @click="onSignUp" :disabled="!email || !password || !verificationCode" class="w-full bg-orange-600 text-neutral-950 h-12 rounded-full text-lg font-semibold my-4">
{{ t('common.signup') }}
                            </v-btn>
                        </div>
                    </v-expand-transition>
                    <div class="text-center">
                        <span class="text-neutral-400 font-medium">{{ t('common.already_account') }} </span>
                        <span class="text-orange-600 text-sm font-medium underline cursor-pointer" @click="router.push(localePath('/login/google'))">{{ t('common.login') }}</span>
                    </div>
                    <div class="desc text-white/60 mt-6">
                        {{ t('common.agree_terms_start') }}
                        <NuxtLink :to="localePath('/terms')" class="underline font-semibold">
                            {{ t('common.terms_service') }}
                        </NuxtLink> {{ t('common.and') }}
                        <NuxtLink :to="localePath('/policy')" class="underline font-semibold">
                            {{ t('common.privacy_policy') }}
                        </NuxtLink>
                    </div>
                </div>
            </div>
        </div>
        <NewFooter />
    </div>
</template>

<style lang="scss" scoped>
:deep(.verification-code .v-input__control .v-field) {
    border: none !important;
    border-radius: 50px 0 0 50px !important;
    overflow: hidden;
}
</style>